/* eslint-disable qwik/no-use-visible-task */
import type { Signal } from "@builder.io/qwik";
import { $, component$, useSignal, useVisibleTask$ } from "@builder.io/qwik";
import { useLocation } from "@builder.io/qwik-city";
import type { IGenBlogSection_Text } from "~/services/graphql/__generated/sdk";
import { cn } from "~/utils/tailwind-helper";

interface IGetSectionsFromText {
    content: any;
    sections?: { id: string; title: string }[];
}

const getSectionsFromText = ({ content, sections = [] }: IGetSectionsFromText) => {
    if (!content) return sections;
    if (content && Array.isArray(content)) {
        content.forEach((item) => {
            if (item.type === "heading" && item.attrs && (item.attrs.level === 2 || item.attrs.level === 1)) {
                const text = item.content?.[0]?.text;
                if (text) {
                    const id = item.attrs?.id ?? "";
                    sections.push({
                        id,
                        title: text,
                    });
                }
            }
        });
    }
    return sections;
};

export interface BlogTableOfContentProps {
    text: IGenBlogSection_Text;
    bodyRef: Signal<HTMLDivElement>;
}

export const BlogTableOfContent = component$(({ bodyRef, text }: BlogTableOfContentProps) => {
    const sections = getSectionsFromText({ content: text.json?.content });
    const activeArticleSection = useSignal<string | null>(null);
    const location = useLocation();

    const updateActiveArticleSection = $((section: string | null) => {
        if ("" + section == "null") return;

        activeArticleSection.value = section;
    });

    const scrollIntoView = $((id: string) => {
        const navHeight = (document.querySelector("#main-nav") as HTMLDivElement)?.offsetHeight || 100;

        const target = document.getElementById(id);

        if (!target) return;
        const scrollToPosition = target.getBoundingClientRect().top + window.scrollY - navHeight;

        window.scrollTo({
            top: scrollToPosition,
            behavior: "smooth",
        });

        window.history.pushState(null, "", `${location.url.pathname.split("#")}#${id}`);
    });

    useVisibleTask$(() => {
        const id = window.location.hash.replace("#", "");

        if (id) {
            scrollIntoView(id);
        }
    });

    useVisibleTask$(({ track, cleanup }) => {
        track(() => bodyRef.value);

        let articleSections = document.querySelectorAll(".article-section");

        const navHeight = (document.querySelector("#main-nav") as HTMLDivElement)?.offsetHeight || 100;

        if (!bodyRef.value) return;

        const getActiveArticleSection = () => {
            if (articleSections.length == 0) {
                articleSections = document.querySelectorAll(".article-section");
            }

            const lastSectionId = articleSections[articleSections.length - 1]?.id;

            let isSectionActive = false;
            if (Math.round(bodyRef.value.offsetHeight) <= Math.round(window.scrollY)) {
                isSectionActive = true;
                return null;
            }

            articleSections.forEach((section, index) => {
                if (isSectionActive) return null;
                const nextSection = articleSections?.[index + 1];
                if (
                    nextSection?.getBoundingClientRect().top - navHeight > 32 ||
                    (!nextSection && section.getBoundingClientRect().top - navHeight > 32)
                ) {
                    updateActiveArticleSection(section.id);
                    isSectionActive = true;
                } else if (!nextSection) {
                    updateActiveArticleSection(lastSectionId || null);
                    isSectionActive = true;
                }
            });
        };
        getActiveArticleSection();
        window.addEventListener("scroll", getActiveArticleSection);

        cleanup(() => window.removeEventListener("scroll", getActiveArticleSection));
    });

    if (!sections || sections.length === 0) return null;

    return (
        <div
            class={cn(
                // 88px (nav) + 64px
                "sticky top-[152px] z-40 row-start-1",
                "col-span-3 col-start-1 hidden h-fit gold:block platinum:max-w-[261px]",
                "rounded-lg bg-white-opacity-80 p-4 backdrop-blur-[30px]",
            )}
        >
            <ul class="flex list-none flex-col gap-2">
                {sections?.map(
                    (section, index) =>
                        section.title && (
                            <li class="group m-0 list-none" key={`side-bar-link${section.id}`}>
                                <button
                                    type="button"
                                    class={cn(
                                        "text-left text-base font-normal text-primary-500/60 transition-colors hover:text-primary-500",
                                        activeArticleSection.value === section.id && "text-primary-500",
                                    )}
                                    onClick$={() => scrollIntoView(`${sections?.[index]?.id}`)}
                                >
                                    {section.title}
                                </button>
                            </li>
                        ),
                )}
            </ul>
        </div>
    );
});
